/**
 * Command Center — Esri satellite hybrid (imagery + roads + places), FH2-like.
 * Pins / AI crowns stay on Marker DOM; this file only owns basemap + marker chrome.
 */

import { ensureGeoLibreRuntimeLoaded } from "../../geolibre-runtime-loader.js";
import {
  resolveCommandCenterMapTheme,
  buildCommandCenterBasemapStyle,
  DASHBOARD_MAP_DEFAULTS,
} from "./map-style.js";
import { applyDigitalTwinMapEnhancements, BUILDING_3D_LAYER_ID, armBuildingExtrusionErrorGuard } from "./building-layer.js";
import { setFleetClusterVisible } from "../../ops-map-fleet-cluster.js";

/** @deprecated vector bright — CC now uses {@link buildCommandCenterBasemapStyle} */
export const CC_MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/bright";

/**
 * @param {object} opts
 * @param {HTMLElement} opts.container
 * @param {{ lng: number; lat: number; zoom: number }} [opts.centerZoom]
 * @param {boolean} [opts.pitch3d]
 */
export async function createCommandCenterMap(opts) {
  const runtime = await ensureGeoLibreRuntimeLoaded();
  const created = await runtime.mountMap(opts.container, {
    centerZoom: opts.centerZoom,
    pitch3d: opts.pitch3d,
  });
  const { map, maplibregl } = created;
  const mapTheme = resolveCommandCenterMapTheme();
  armBuildingExtrusionErrorGuard(map);
  map.once("load", () => {
    applyDigitalTwinMapEnhancements(map, mapTheme);
  });

  return { map, maplibregl };
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {boolean} enabled
 */
export function setCommandCenterMap3d(map, enabled) {
  if (!map) return;
  try {
    map.easeTo({
      pitch: enabled ? DASHBOARD_MAP_DEFAULTS.pitch3d : 0,
      bearing: enabled ? DASHBOARD_MAP_DEFAULTS.bearing3d : 0,
      duration: 600,
    });
    if (enabled) {
      applyDigitalTwinMapEnhancements(map, resolveCommandCenterMapTheme());
    }
  } catch (_) {}
}

/**
 * Day satellite ↔ night darkened satellite (same imagery + label overlays).
 * @param {import('maplibre-gl').Map | null} map
 * @param {boolean} night
 * @returns {Promise<void>}
 */
export async function setCommandCenterMapNight(map, night) {
  if (!map || typeof map.setStyle !== "function") return;
  const theme = night ? "night" : "day";
  const styleSpec = buildCommandCenterBasemapStyle({ theme });
  const center = map.getCenter();
  const zoom = map.getZoom();
  const pitch = map.getPitch();
  const bearing = map.getBearing();
  map.setStyle(styleSpec);
  map.once("style.load", () => {
    try {
      map.jumpTo({ center, zoom, pitch, bearing });
    } catch (_) {}
    try {
      applyDigitalTwinMapEnhancements(map, theme);
    } catch (_) {}
  });
}

/**
 * Self-contained CC marker DOM (image-exact colors: green/red/gray).
 * Does not depend on ops-map injected CSS — styles live in cc-styles.js.
 * @param {string} label
 * @param {"online"|"offline"|"alert"} pulse
 * @param {{ aiThreat?: boolean, aiScore?: number|null, aiBand?: string }} [opts]
 * @returns {HTMLElement}
 */
export function buildCcMarkerElement(label, pulse, opts = {}) {
  const p = pulse === "alert" || pulse === "online" || pulse === "offline" ? pulse : "offline";
  const anchor = document.createElement("div");
  anchor.className = `cc-marker cc-marker--${p}`;
  anchor.setAttribute("role", "button");
  anchor.tabIndex = 0;
  anchor.title = String(label || "Device");
  const crown = document.createElement("span");
  crown.className = "cc-marker__crown";
  crown.setAttribute("aria-hidden", "true");
  crown.hidden = true;
  const ring = document.createElement("span");
  ring.className = "cc-marker__ring";
  ring.setAttribute("aria-hidden", "true");
  const core = document.createElement("span");
  core.className = "cc-marker__core";
  core.setAttribute("aria-hidden", "true");
  const cap = document.createElement("span");
  cap.className = "cc-marker__cap";
  cap.textContent = String(label || "");
  anchor.appendChild(crown);
  anchor.appendChild(ring);
  anchor.appendChild(core);
  anchor.appendChild(cap);
  updateCcMarkerAiThreat(anchor, opts);
  return anchor;
}

/**
 * Update an existing CC marker element in place (no maplibre setElement).
 * @param {HTMLElement} el
 * @param {string} label
 * @param {"online"|"offline"|"alert"} pulse
 * @param {{ aiThreat?: boolean, aiScore?: number|null, aiBand?: string }} [opts]
 */
export function updateCcMarkerElement(el, label, pulse, opts = {}) {
  if (!el) return;
  const p = pulse === "alert" || pulse === "online" || pulse === "offline" ? pulse : "offline";
  el.classList.remove("cc-marker--online", "cc-marker--offline", "cc-marker--alert");
  el.classList.add(`cc-marker--${p}`);
  el.title = String(label || "Device");
  const cap = el.querySelector(".cc-marker__cap");
  if (cap) cap.textContent = String(label || "");
  if (!el.querySelector(".cc-marker__crown")) {
    const crown = document.createElement("span");
    crown.className = "cc-marker__crown";
    crown.setAttribute("aria-hidden", "true");
    crown.hidden = true;
    el.insertBefore(crown, el.firstChild);
  }
  updateCcMarkerAiThreat(el, opts);
}

/**
 * ONCR entity crown — what the system currently believes about this unit.
 * Falls back to the legacy risk-score crown when no hypothesis is persisted.
 * @param {HTMLElement|null} el
 * @param {{ aiThreat?: boolean, aiScore?: number|null, aiBand?: string, hypothesis?: unknown }} [opts]
 */
export function updateCcMarkerAiThreat(el, opts = {}) {
  if (!el) return;
  const on = !!opts.aiThreat;
  el.classList.toggle("cc-marker--ai-threat", on);
  el.setAttribute("data-ai-threat", on ? "1" : "0");
  let crown = el.querySelector(".cc-marker__crown");
  if (!crown) {
    crown = document.createElement("span");
    crown.className = "cc-marker__crown";
    crown.setAttribute("aria-hidden", "true");
    el.insertBefore(crown, el.firstChild);
  }
  if (!on) {
    crown.hidden = true;
    crown.textContent = "";
    crown.classList.remove("cc-marker__crown--critical");
    el.removeAttribute("data-oncr-hypothesis");
    return;
  }
  const hyp = opts.hypothesis && typeof opts.hypothesis === "object" ? opts.hypothesis : null;
  const score = Number(opts.aiScore);
  const band = String(opts.aiBand || "").trim();
  const scoreTxt = Number.isFinite(score) ? String(Math.round(score)) : "";
  const hypText = hyp ? String(hyp.text || "").trim() : "";
  crown.hidden = false;
  crown.textContent = hypText || (scoreTxt ? `AI · ${scoreTxt}` : band || "AI · Needs you");
  const critical = hyp ? String(hyp.band || "") === "critical" : Number.isFinite(score) && score >= 70;
  if (critical) crown.classList.add("cc-marker__crown--critical");
  else crown.classList.remove("cc-marker__crown--critical");
  if (hyp && hyp.code) el.setAttribute("data-oncr-hypothesis", String(hyp.code));
  else el.removeAttribute("data-oncr-hypothesis");
}

const ZONE_SOURCE = "cc-zone-polygons";
const ZONE_FILL = "cc-zone-fill";
const ZONE_LINE = "cc-zone-line";

const ZONE_COLORS = [
  "rgba(59, 130, 246, 0.18)",
  "rgba(139, 92, 246, 0.18)",
  "rgba(34, 197, 94, 0.18)",
  "rgba(245, 158, 11, 0.18)",
];

/**
 * Build simple bbox polygons per group from device coords (real data, not geofence API).
 * @param {Array<{ map_lat?: number; map_lng?: number; map_group_route_key?: string; label?: string }>} items
 */
export function buildGroupZoneGeoJson(items) {
  /** @type {Record<string, { lats: number[]; lngs: number[]; label: string }>} */
  const buckets = {};
  for (const row of items || []) {
    const lat = Number(row.map_lat);
    const lng = Number(row.map_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const key = String(row.map_group_route_key || "ungrouped").trim() || "ungrouped";
    if (!buckets[key]) buckets[key] = { lats: [], lngs: [], label: key };
    buckets[key].lats.push(lat);
    buckets[key].lngs.push(lng);
  }
  const features = [];
  let i = 0;
  for (const [key, b] of Object.entries(buckets)) {
    if (b.lats.length < 2) continue;
    const minLat = Math.min(...b.lats);
    const maxLat = Math.max(...b.lats);
    const minLng = Math.min(...b.lngs);
    const maxLng = Math.max(...b.lngs);
    const pad = 0.0008;
    features.push({
      type: "Feature",
      properties: { group: key, colorIdx: i % ZONE_COLORS.length, label: b.label },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [minLng - pad, minLat - pad],
          [maxLng + pad, minLat - pad],
          [maxLng + pad, maxLat + pad],
          [minLng - pad, maxLat + pad],
          [minLng - pad, minLat - pad],
        ]],
      },
    });
    i += 1;
  }
  return { type: "FeatureCollection", features };
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {object} geojson
 */
export function upsertCommandCenterZones(map, geojson) {
  if (!map || !map.isStyleLoaded()) return;
  try {
    if (map.getSource(ZONE_SOURCE)) {
      map.getSource(ZONE_SOURCE).setData(geojson);
      return;
    }
    map.addSource(ZONE_SOURCE, { type: "geojson", data: geojson });
    map.addLayer({
      id: ZONE_FILL,
      type: "fill",
      source: ZONE_SOURCE,
      paint: {
        "fill-color": [
          "match",
          ["%", ["get", "colorIdx"], 4],
          0, ZONE_COLORS[0],
          1, ZONE_COLORS[1],
          2, ZONE_COLORS[2],
          ZONE_COLORS[3],
        ],
        "fill-opacity": 0.55,
      },
    });
    map.addLayer({
      id: ZONE_LINE,
      type: "line",
      source: ZONE_SOURCE,
      paint: {
        "line-color": "#1890ff",
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    });
  } catch (_) {}
}

const INCIDENT_SOURCE = "cc-incident-focus";
const INCIDENT_PULSE = "cc-incident-pulse";
const INCIDENT_RISK_OUTER = "cc-incident-risk-outer";
const INCIDENT_RISK_INNER = "cc-incident-risk-inner";
const HEAT_SOURCE = "cc-event-heat";
const HEAT_LAYER = "cc-event-heat-circles";

/**
 * Potout / incident center pulse + risk rings (real coords only).
 * @param {import('maplibre-gl').Map | null} map
 * @param {{ lng: number; lat: number; riskScore?: number } | null} focus
 */
export function upsertIncidentFocusLayer(map, focus) {
  if (!map || !map.isStyleLoaded()) return;
  const empty = { type: "FeatureCollection", features: [] };
  const pt =
    focus && Number.isFinite(focus.lng) && Number.isFinite(focus.lat)
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {
                risk: Number.isFinite(Number(focus.riskScore)) ? Number(focus.riskScore) : 50,
              },
              geometry: { type: "Point", coordinates: [focus.lng, focus.lat] },
            },
          ],
        }
      : empty;
  try {
    if (!map.getSource(INCIDENT_SOURCE)) {
      map.addSource(INCIDENT_SOURCE, { type: "geojson", data: pt });
      map.addLayer({
        id: INCIDENT_RISK_OUTER,
        type: "circle",
        source: INCIDENT_SOURCE,
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["coalesce", ["to-number", ["get", "risk"]], 50],
            0,
            40,
            100,
            70,
          ],
          "circle-color": "rgba(255, 59, 48, 0.12)",
          "circle-stroke-color": "rgba(255, 59, 48, 0.35)",
          "circle-stroke-width": 2,
        },
      });
      map.addLayer({
        id: INCIDENT_RISK_INNER,
        type: "circle",
        source: INCIDENT_SOURCE,
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["coalesce", ["to-number", ["get", "risk"]], 50],
            0,
            22,
            100,
            55,
          ],
          "circle-color": "rgba(255, 59, 48, 0.28)",
          "circle-stroke-color": "#FF3B30",
          "circle-stroke-width": 2,
        },
      });
      map.addLayer({
        id: INCIDENT_PULSE,
        type: "circle",
        source: INCIDENT_SOURCE,
        paint: {
          "circle-radius": 12,
          "circle-color": "#FF3B30",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
        },
      });
    } else {
      map.getSource(INCIDENT_SOURCE).setData(pt);
    }
  } catch (_) {}
}

/**
 * Event density heat from events matched to map-snapshot device coords.
 * @param {import('maplibre-gl').Map | null} map
 * @param {object[]} events
 * @param {object[]} mapItems
 */
export function upsertEventHeatLayer(map, events, mapItems) {
  if (!map || !map.isStyleLoaded()) return;
  const byDev = new Map();
  for (const row of mapItems || []) {
    const id = String(row.device_id || row.marker_ref || "").trim();
    const lat = Number(row.map_lat);
    const lng = Number(row.map_lng);
    if (id && Number.isFinite(lat) && Number.isFinite(lng)) byDev.set(id, { lat, lng });
  }
  const features = [];
  for (const ev of events || []) {
    const did = String(ev.device_id || "").trim();
    const hit = byDev.get(did);
    if (!hit) continue;
    features.push({
      type: "Feature",
      properties: { weight: 1 },
      geometry: { type: "Point", coordinates: [hit.lng, hit.lat] },
    });
  }
  const geo = { type: "FeatureCollection", features };
  try {
    if (!map.getSource(HEAT_SOURCE)) {
      map.addSource(HEAT_SOURCE, { type: "geojson", data: geo });
      map.addLayer(
        {
          id: HEAT_LAYER,
          type: "circle",
          source: HEAT_SOURCE,
          paint: {
            "circle-radius": 22,
            "circle-color": "rgba(249, 115, 22, 0.22)",
            "circle-blur": 0.6,
          },
        },
        ZONE_FILL,
      );
    } else {
      map.getSource(HEAT_SOURCE).setData(geo);
    }
  } catch (_) {}
}

/**
 * Toggle map layer visibility from MapControls checkboxes.
 * @param {import('maplibre-gl').Map | null} map
 * @param {Map<string, import('maplibre-gl').Marker>} markers
 * @param {Record<string, boolean>} visibility
 * @param {boolean} [clusterActive]
 */
export function setDashboardLayerVisibility(map, markers, visibility, clusterActive = false) {
  if (!map) return;
  const vis = (id, on) => {
    try {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
    } catch (_) {}
  };
  const v = visibility || {};
  vis(ZONE_FILL, v.zones !== false);
  vis(ZONE_LINE, v.zones !== false);
  vis(HEAT_LAYER, !!v.heatmap);
  vis(INCIDENT_PULSE, v.incidents !== false);
  vis(INCIDENT_RISK_OUTER, v.incidents !== false);
  vis(INCIDENT_RISK_INNER, v.incidents !== false);
  vis(BUILDING_3D_LAYER_ID, v.buildings !== false);
  const showMarkers = v.devices !== false || v.cameras !== false || v.sensors !== false;
  if (clusterActive) {
    setFleetClusterVisible(map, showMarkers);
  } else if (markers) {
    for (const mk of markers.values()) {
      try {
        const el = mk.getElement();
        if (el) el.style.display = showMarkers ? "" : "none";
      } catch (_) {}
    }
  }
}

